import type { Region } from '../model/types';

async function decode(blob: Blob): Promise<{ img: HTMLImageElement; done: () => void }> {
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.src = url;
  try {
    await img.decode();
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
  return { img, done: () => URL.revokeObjectURL(url) };
}

/**
 * The square to crop for a profile photo: centered on `region` (a face in a group picture)
 * with some room around it, or the middle of the picture.
 */
export function photoSquare(width: number, height: number, region?: Region): { left: number; top: number; side: number } {
  const max = Math.min(width, height);
  if (!region) return { left: (width - max) / 2, top: (height - max) / 2, side: max };
  const side = Math.min(max, Math.max(region.w * width, region.h * height) * 1.35);
  const cx = (region.x + region.w / 2) * width;
  const cy = (region.y + region.h / 2) * height;
  const clamp = (v: number, hi: number) => Math.min(Math.max(v, 0), hi);
  return { left: clamp(cx - side / 2, width - side), top: clamp(cy - side / 2, height - side), side };
}

/** Square-crop and shrink an image to a small JPEG data URL, so trees stay light. */
export async function photoFromBlob(blob: Blob, size = 256, region?: Region): Promise<string> {
  const { img, done } = await decode(blob);
  try {
    const { left, top, side } = photoSquare(img.naturalWidth, img.naturalHeight, region);
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = Math.max(1, Math.round(Math.min(size, side)));
    canvas.getContext('2d')!.drawImage(img, left, top, side, side, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.85);
  } finally {
    done();
  }
}

/** A gallery thumbnail: the whole image, at most `max` pixels on its longer side. */
export async function thumbFromBlob(blob: Blob, max = 320): Promise<string> {
  const { img, done } = await decode(blob);
  try {
    const scale = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.8);
  } finally {
    done();
  }
}
